import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface MapComponentProps {
  center: [number, number];
  zoom?: number;
  markers?: Array<{
    position: [number, number];
    color: 'green' | 'red' | 'blue';
    popup: string;
    isPulsing?: boolean;
  }>;
  routePath?: [number, number][];
  className?: string;
}

export function MapComponent({
  center,
  zoom = 5,
  markers = [],
  routePath,
  className = 'h-full w-full',
}: MapComponentProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  // The position the map opens at. Changes after mount are handled by the
  // effect below, which calls setView; re-creating the map for them would
  // throw away every layer on it.
  const initialView = useRef({ center, zoom });
  const mapInstanceRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Initialize map
    const map = L.map(mapRef.current, {
      scrollWheelZoom: true,
      zoomControl: true,
    }).setView(initialView.current.center, initialView.current.zoom);

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const map = mapInstanceRef.current;

    // Clear existing markers and polylines
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker || layer instanceof L.Polyline) {
        map.removeLayer(layer);
      }
    });

    // Add markers
    markers.forEach((marker) => {
      const icon = marker.isPulsing ? createPulsingIcon() : createIcon(marker.color);
      const markerInstance = L.marker(marker.position, { icon }).addTo(map);
      markerInstance.bindPopup(marker.popup);
    });

    // Add route path
    if (routePath && routePath.length > 1) {
      L.polyline(routePath, {
        color: '#3b82f6',
        weight: 3,
        dashArray: '10, 10',
      }).addTo(map);
    }

    // Update map center
    map.setView(center, zoom);
  }, [center, zoom, markers, routePath]);

  return <div ref={mapRef} className={className} style={{ zIndex: 0 }} />;
}

const createIcon = (color: 'green' | 'red' | 'blue'): L.DivIcon => {
  const colors = {
    green: '#22c55e',
    red: '#ef4444',
    blue: '#3b82f6',
  };

  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${colors[color]}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

const createPulsingIcon = (): L.DivIcon => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="position: relative;">
        <div style="width: 24px; height: 24px; background-color: #3b82f6; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); position: relative; z-index: 10;"></div>
        <div style="position: absolute; top: 0; left: 0; width: 24px; height: 24px; background-color: #3b82f6; border-radius: 50%; animation: pulse 2s infinite; opacity: 0.6;"></div>
      </div>
      <style>
        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.5); opacity: 0.3; }
          100% { transform: scale(2); opacity: 0; }
        }
      </style>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};
